import { FOLDER_PURPOSES, FOLDER_PURPOSE_LABELS, folderPurposeOf } from '@/lib/folderPurpose';
import type { Folder, FolderPurpose, FolderStatus } from '../api/types';

/**
 * Pure folder helpers for the sidebar and the folder header: lifecycle status,
 * the one count a row shows, grouping, sorting and the owner filter.
 */

/** Lifecycle status, falling back to `archivedAt` on servers that predate it. */
export function folderStatus(f: Pick<Folder, 'status' | 'archivedAt'>): FolderStatus {
  if (f.status) return f.status;
  return f.archivedAt ? 'archived' : 'active';
}

export { FOLDER_PURPOSES, FOLDER_PURPOSE_LABELS };

/** What the folder is for; clipping when the server didn't say. */
export function folderPurpose(f: Pick<Folder, 'purpose'>): FolderPurpose {
  return folderPurposeOf(f.purpose);
}

export const FOLDER_STATUS_LABELS: Record<FolderStatus, string> = {
  active: 'Active',
  complete: 'Complete',
  archived: 'Archived',
};

/** Leads still needing work. Prefers `counts.open`, then the older untouched count. */
export function openLeadCount(f: Pick<Folder, 'counts' | 'untouchedCount' | 'leadCount'>): number {
  if (f.counts) return f.counts.open;
  if (typeof f.untouchedCount === 'number') return f.untouchedCount;
  return f.leadCount ?? 0;
}

export function totalLeadCount(f: Pick<Folder, 'counts' | 'leadCount'>): number {
  return f.counts?.total ?? f.leadCount ?? 0;
}

export function ownerOf(f: Pick<Folder, 'ownerName' | 'createdByName'>): string | null {
  return f.ownerName ?? f.createdByName ?? null;
}

export type FolderSort = 'recent' | 'name' | 'open';

export const FOLDER_SORT_LABELS: Record<FolderSort, string> = {
  recent: 'Recent activity',
  name: 'Name',
  open: 'Open leads',
};

function activityAt(f: Folder): number {
  const t = Date.parse(f.updatedAt ?? f.createdAt);
  return Number.isFinite(t) ? t : 0;
}

export function sortFolders(folders: readonly Folder[], sort: FolderSort): Folder[] {
  const byName = (a: Folder, b: Folder) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
  return [...folders].sort((a, b) => {
    if (sort === 'name') return byName(a, b);
    if (sort === 'open') return openLeadCount(b) - openLeadCount(a) || byName(a, b);
    return activityAt(b) - activityAt(a) || byName(a, b);
  });
}

/** `''` = everyone, `'mine'` = folders the current user created, else an owner name. */
export type OwnerFilter = string;

export function filterFolders(
  folders: readonly Folder[],
  opts: { owner?: OwnerFilter; currentUserId?: string | null; search?: string; purpose?: FolderPurpose | '' },
): Folder[] {
  const q = (opts.search ?? '').trim().toLowerCase();
  return folders.filter((f) => {
    if (q && !f.name.toLowerCase().includes(q)) return false;
    if (opts.purpose && folderPurpose(f) !== opts.purpose) return false;
    if (!opts.owner) return true;
    if (opts.owner === 'mine') return !!opts.currentUserId && f.createdByUserId === opts.currentUserId;
    return ownerOf(f) === opts.owner;
  });
}

export function ownerNames(folders: readonly Folder[]): string[] {
  const names = new Set<string>();
  for (const f of folders) {
    const o = ownerOf(f);
    if (o) names.add(o);
  }
  return [...names].sort((a, b) => a.localeCompare(b));
}

export type FolderGroupId = 'pinned' | 'active' | 'complete' | 'archived';

export const FOLDER_GROUPS: { id: FolderGroupId; label: string }[] = [
  { id: 'pinned', label: 'Pinned' },
  { id: 'active', label: 'Active' },
  { id: 'complete', label: 'Complete' },
  { id: 'archived', label: 'Archived' },
];

/** Archived is folded until someone opens it. */
export const DEFAULT_COLLAPSED_GROUPS: FolderGroupId[] = ['archived'];

/**
 * Pinned wins over Active/Complete, but an archived folder is always under
 * Archived — a pin shouldn't drag a shelved folder back into the working set.
 */
export function folderGroupOf(f: Folder): FolderGroupId {
  const status = folderStatus(f);
  if (status === 'archived') return 'archived';
  if (f.pinnedAt) return 'pinned';
  return status;
}

export function groupFolders(folders: readonly Folder[], sort: FolderSort): Record<FolderGroupId, Folder[]> {
  const out: Record<FolderGroupId, Folder[]> = { pinned: [], active: [], complete: [], archived: [] };
  for (const f of sortFolders(folders, sort)) out[folderGroupOf(f)].push(f);
  return out;
}

const DAY = 86_400_000;

/**
 * Whole days until a Complete folder is archived automatically, or null when
 * it isn't on that path (not complete, auto-archive snoozed by a restore).
 * Never negative: an overdue folder archives on the next sweep, so "0".
 */
export function archivesInDays(
  f: Pick<Folder, 'status' | 'archivedAt' | 'completeSince' | 'archiveSnoozedUntil'>,
  afterDays: number,
  now: number = Date.now(),
): number | null {
  if (folderStatus(f) !== 'complete' || !f.completeSince) return null;
  const since = Date.parse(f.completeSince);
  if (!Number.isFinite(since)) return null;
  let due = since + afterDays * DAY;
  if (f.archiveSnoozedUntil) {
    const snooze = Date.parse(f.archiveSnoozedUntil);
    if (Number.isFinite(snooze)) due = Math.max(due, snooze);
  }
  return Math.max(0, Math.ceil((due - now) / DAY));
}

export function archivesInLabel(days: number | null): string | null {
  if (days === null) return null;
  if (days === 0) return 'Archives today';
  return `Archives in ${days} day${days === 1 ? '' : 's'}`;
}
